/**
 * Centralised page-meta builder. Mirrors the full tag set the homepage hand-
 * writes (title, description, canonical, Open Graph, Twitter) so every
 * programmatic template ships complete social + AI-crawler metadata instead
 * of just og:image.
 *
 * og:type / og:site_name / og:locale / twitter:card are emitted once in the
 * root <head>, so they are intentionally NOT repeated here.
 */

const SITE = 'https://www.afficheservices.fr'

export interface SeoMetaInput {
  /** <title> + default og/twitter title. */
  title: string
  /** meta description + default og/twitter description. */
  description: string
  /** Canonical path, leading slash, no host. e.g. '/ville/paris'. */
  path: string
  /** Override social title (defaults to `title`). */
  socialTitle?: string
  /** Override social description (defaults to `description`). */
  socialDescription?: string
  /** Full image URL. Defaults to the static branded PNG (social-safe). */
  image?: string
  /** @deprecated kept for call-site compatibility; no longer used for og. */
  imageTitle?: string
  /** @deprecated kept for call-site compatibility; no longer used for og. */
  imageSubtitle?: string
  /** Emit robots noindex,follow (for thin/funnel pages). */
  noindex?: boolean
}

type MetaDescriptor =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string }
  | { tagName: 'link'; rel: string; href: string }

export function seoMeta(input: SeoMetaInput): MetaDescriptor[] {
  const url = `${SITE}${input.path}`
  const socialTitle = input.socialTitle ?? input.title
  const socialDescription = input.socialDescription ?? input.description
  // Static branded PNG by default — social platforms (FB/Twitter/LinkedIn)
  // reject SVG og:image, so the old /api/og SVG card never previewed. A real
  // PNG renders everywhere. og:image isn't a search-ranking factor.
  const image = input.image ?? `${SITE}/og-default.png`

  const tags: MetaDescriptor[] = [
    { title: input.title },
    { name: 'description', content: input.description },
    { tagName: 'link', rel: 'canonical', href: url },
    { property: 'og:title', content: socialTitle },
    { property: 'og:description', content: socialDescription },
    { property: 'og:url', content: url },
    { property: 'og:image', content: image },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:title', content: socialTitle },
    { name: 'twitter:description', content: socialDescription },
  ]

  if (input.noindex) tags.push({ name: 'robots', content: 'noindex,follow' })

  return tags
}
